import { Axios } from "../config";
import { GET, PUT } from "../config";

/**
 * Obtener la configuración global de la aplicación (logo, colores, etc.)
 * @returns {Promise<Object|null>} Configuración global o null si hay error
 */
export const getGlobalConfig = async () => {
  try {
    // ✅ Ruta correcta: api/shared/config/global/ (usando namespace shared)
    const response = await Axios.get("shared/config/global/");
    return response.data;
  } catch (error) {
    // ✅ Silenciar error 404 si el endpoint no existe aún
    if (error.response?.status === 404) {
      return null;
    }
    return null;
  }
};

export const updateGlobalConfig = async (data) => {
  try {
    const formData = new FormData();
    if (data.primary_color)
      formData.append("primary_color", data.primary_color);
    if (data.secondary_color)
      formData.append("secondary_color", data.secondary_color);
    if (data.app_name)
      formData.append("app_name", data.app_name);
    if (data.global_logo) formData.append("global_logo", data.global_logo);
    
    // ✅ NUEVO: Soporte para system_settings (configuración SimpleAPI y otras)
    if (data.system_settings) {
      formData.append("system_settings", JSON.stringify(data.system_settings));
    }

    const response = await Axios.put("shared/config/global/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Obtener la configuración POS del usuario actual
 * @returns {Promise<Object>} Configuración POS o valores por defecto
 */
export const getPOSConfig = async () => {
  try {
    // Ruta: /api/shared/config/pos_settings/ (router + viewset + action)
    const response = await GET("shared/config/pos_settings/");
    // El backend retorna directamente el objeto de configuración
    return response.data || response;
  } catch (error) {
    // Si el endpoint no existe, retornar valores por defecto
    if (error.response?.status === 404 || error.response?.status === 401) {
      console.warn("POS config endpoint not found or unauthorized, using defaults");
      return getDefaultPOSConfig();
    }
    console.error("Error fetching POS config:", error);
    return getDefaultPOSConfig();
  }
};

/**
 * Guardar la configuración POS del usuario actual
 * @param {Object} data - Datos de configuración POS
 * @returns {Promise<Object>} Configuración guardada
 */
export const updatePOSConfig = async (data) => {
  try {
    // Ruta: /api/shared/config/pos_settings/ (router + viewset + action)
    const response = await PUT("shared/config/pos_settings/", data);
    // El backend retorna { message, config }
    return response.data?.config || response.data || data;
  } catch (error) {
    // Si el endpoint no existe, guardar en localStorage como fallback
    if (error.response?.status === 404 || error.response?.status === 401) {
      console.warn("POS config endpoint not found or unauthorized, saving to localStorage");
      localStorage.setItem('pos_settings', JSON.stringify(data));
      return data;
    }
    console.error("Error updating POS config:", error);
    throw error;
  }
};

/**
 * Listar logs de auditoría
 * @param {Object} params - Parámetros de filtro
 */
export const listAuditLogs = (params) => Axios.get("audit/logs/", { params });

/**
 * Obtener valores por defecto para la configuración POS
 * @returns {Object} Configuración por defecto
 */
const getDefaultPOSConfig = () => {
  // Intentar cargar desde localStorage primero
  try {
    const stored = localStorage.getItem('pos_settings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn("Error parsing stored POS config:", error);
  }
  
  // Valores por defecto
  return {
    default_raw_material: null,
    default_branch: null,
    auto_select_product: true,
    show_price_in_selector: true,
    require_branch_selection: false,
    default_measurement_unit: "unidad",
    show_product_images: true,
    enable_quick_actions: true,
  };
};

/**
 * Obtener configuración temática de la sucursal predeterminada del usuario
 * @returns {Promise<Object>} Configuración temática de la sucursal predeterminada
 */
export const getMyDefaultBranchTheme = async () => {
  // ⚠️ Endpoint no implementado en backend - retornando valores por defecto
  // Para personalización real, implementar endpoint en branches/views
  return {
    app_name: "ERP System",
    primary_color: "#000000",
    secondary_color: "#FFFFFF",
    font_size: 14,
    component_size: "middle",
    borderRadius: 6,
    motion: true,
    compact: false,
    algorithm: "light",
    logo_url: null,
  };
};

/**
 * Probar conexión con SimpleAPI.cl
 * @param {Object} data - Credenciales a probar (api_key, rut_empresa, base_url)
 * @returns {Promise<Object>} Resultado de la prueba
 */
export const testSimpleAPI = async (data) => {
  return Axios.post("shared/config/test-simple-api/", data);
};

/**
 * API para Configuraciones de Módulos (Global y por Sucursal)
 */
export const moduleConfigs = {
  list: (params) => Axios.get("shared/module-configs/", { params }),
  get: (id) => Axios.get(`shared/module-configs/${id}/`),
  create: (data) => Axios.post("shared/module-configs/", data),
  update: (id, data) => Axios.put(`shared/module-configs/${id}/`, data),
  delete: (id) => Axios.delete(`shared/module-configs/${id}/`),
};
